import { useState, useEffect } from "react";
import Popup from "../ui/Popup";
import VirtualTable from "../ui/VirtualTable";
import { useTabs } from "../hooks/useTabs";
import { listen } from "../hooks/Listener";
import { KBD } from "../utils/Keybindings";
import { pubCall } from "../utils/PubSub";
import { showError, showMessage } from "../ui/Message";
import { InfoIcon } from "../utils/Icons";
import Status from "../ui/Status";
import Nav from "../ui/Nav";
import Input from "../ui/Input";
import { ROUTES } from "../utils/Routes";
import { createModal } from "../hooks/createModal";
import Confirm from "../popups/Confirm";

export default function OrderSection(props) {

    const NEW_ROW = { account: "", __new: true };

    const { controller = "", callback = () => { }, signal = {}, value = "", route = "" } = props;
    const [data, setData] = useState([]);
    const [align, setAlign] = useState("auto");
    const [selected, setSelected] = useState("");
    const [loading, setLoading] = useState(false);

    const [state, setState] = useState({
        edit: false,
        new: false,
    })

    const tableData = state.new ? [...data, NEW_ROW] : data;

    const isNewRow = (row) => row?.__new === true;

    const defaultTabs = [
        { label: "Se", key: "account", value: "" },
        { label: "Description", key: "description", value: "" },
        { label: "Subaccount", key: "subaccount", value: "" },
    ]
    
    const {
        tabs, setTabs, index, setIndex, update, getVal, editProps, next, prev, refs
    } = useTabs(defaultTabs, "account");
    
    const getAllSections = () => {
        pubCall("general_ledger.getSections", {}, (_s, _r) => {
            if (_s) {
                setData(_r);
                setSelected(_r.length > 0 ? _r[0] : {});
            }
        })
    }

    const drawMessage = () => {
        switch (index) { 
            case "account": return <>A two character/digit code to sub-categorize GL Accts and group Classes</>;
            case "description": return <>A description for this Section code</>;
            case "subaccount": return <>If this section's GL 140 total is derived from Inventory OnHand levels</>;        }
    }

    useEffect(() => {
        getAllSections();
    }, [])

    const canEditRow = (row) => {
        if (isNewRow(row) && state?.new) return true;

        if (state?.edit && row?.id === selected?.id) return true;

        return false;
    }

    const isYes = (_bool) => {
        return (_bool) ? <small style={{ "color": "var(--text)" }}>Yes</small> : <small style={{ "color": "var(--text-dim)" }}>No</small>;
    }



    const columns = [
        { key: "account", title: "Se", align: "left", width: 50, render: (e, f) => {
            if (canEditRow(f)) {
                return <Input maxLength={2} tabMax={() => next()} className="fill" {...editProps("account")} />
            } else {
                return <>{f.account}</>
            }
        }},
        { key: "description", title: "Description", width: 460, align: "left", render: (e, f) => {
            if (canEditRow(f)) {
                return <Input maxLength={50} tabMax={() => next()} className="fill" style={{ "width": "460px"}} {...editProps("description")} />
            } else {
                return <>{f.description}</>
            }
        }},
        { key: "subaccount", title: "Uses O/H", align: "left", width: 80, render: (e, f) => {
            if (canEditRow(f)) {
                return <Input maxLength={1} uppercase={true} chars={["Y", "N"]} className="fill" {...editProps("subaccount")} />
            } else {
                return isYes(f.subaccount);
            }
        }},
    ]

    const removeAccount = () => {
        window.bm_state.value = ROUTES.HELPERS.CONFIRM;
        createModal({
            alert: true,
            component: <Confirm controller={controller} events={[KBD.D, KBD.U]} title={"Remove Section Account?"} route={ROUTES.HELPERS.CONFIRM}/>, closable:true, callback: (e) => {
                window.bm_state.value = route;
                if (e) {
                    pubCall("general_ledger.deleteSection", { id: selected?.id }, (_s, _r) => {
                        if (!_s) return showError(_r);
                        const newData = data.filter(item => item.id !== selected?.id);
                        setData(newData);
                        if (newData.length > 0) {
                            setSelected(newData[0]);
                        } else {
                            setSelected({});
                        }
                })
                }
            }
        })
    }

    const edit_nav = [
        { key: KBD.ESCAPE, action: () => { cancelEditRow() } },
        { key: KBD.ENTER, action: () => index === "subaccount" ? saveSection() : next() },
        { key: KBD.ARROW_DOWN, action: () => next() },
        { key: KBD.ARROW_UP, action: () => prev() },
        { key: KBD.PAGE_DOWN, action: () => { saveSection() } },

    ]

    const nav = [
        { key: KBD.ESCAPE, action: (e) => { callback(false) } },
        { key: KBD.ENTER, action: (e) => { callback(selected) } },
        { key: KBD.D, text: "Del", action: (e) => { removeAccount() } },
        { key: KBD.E, text: "Edt", action: (e) => { showEditRow() } },
        { key: KBD.N, text: "New", action: (e) => { showNewRow() } },
        { key: KBD.ARROW_DOWN, action: (e) => { nextSection() } },
        { key: KBD.ARROW_UP, action: (e) => { prevSection() } },
    ]

    useEffect(() => {
        const actions = (state.new || state.edit) ? edit_nav : nav;
        const cleanup = listen(controller, route, actions, false, [KBD.ESCAPE, KBD.ENTER, KBD.ARROW_DOWN, KBD.ARROW_UP, KBD.PAGE_DOWN]);
        return cleanup;
    }, [index, tabs, refs, state.new, state.edit, selected, data])

    const saveSection = () => {
        const _info = {
            account: getVal("account"),
            description: getVal("description"),
            subaccount: getVal("subaccount") === "Y" ? true : (getVal("subaccount") === "N" ? false : null),
            id: selected?.id === undefined ? "" : selected?.id
        }

        if (state.edit) {
            pubCall("general_ledger.editSection", { id: selected?.id, section_info: _info }, (_s, _r) => {
                if (_s) {
                    showMessage("Section updated");
                    setState(s => ({ ...s, edit: false, new: false }));
                    getAllSections();
                } else {
                    showError(_r);
                }
            })
        } else if (state.new) {
            pubCall("general_ledger.createSection", {  section_info: _info }, (_s, _r) => {
                if (_s) {
                    showMessage("Section created");
                    setState(s => ({ ...s, edit: false, new: false }));
                    getAllSections();
                } else {
                    showError(_r);
                }
            })
        }
    }

    const cancelEditRow = () => {
        setState(s => ({ ...s, edit: false, new: false }));
        setTabs(defaultTabs);
        if (data.length > 0) {
            setSelected(data[0]);
        }
    }

    const showNewRow = () => {
        setState(s => ({ ...s, edit: false, new: true }));
        setSelected(NEW_ROW);
        setIndex("account");    
        requestAnimationFrame(() => {
            refs.current?.account?.focus?.();
        })
    }

    useEffect(() => {
        if (!state.new && !state.edit) return;
        requestAnimationFrame(() => {
            const el = refs?.current[index];
            if (el) el.focus();
        });
    }, [index]);

    const nextSection = (_rows = 1) => {
        if (data.length === 0) { setSelected({}); return; }
        const nextIndex = data.findIndex(item => item?.id === selected?.id) + _rows;
        if (data.length <= nextIndex) { setSelected(data[0]); }
        if (nextIndex < data.length) { setSelected(data[nextIndex]); }
    };

    const prevSection = (_rows = 1) => {
        if (data.length === 0) { setSelected({}); return; }
        const prevIndex = data.findIndex(item => item?.id === selected?.id) - _rows;
        if (prevIndex < 0) { setSelected(data[data.length - 1]); return; }
        if (prevIndex >= 0) { setSelected(data[prevIndex]); }
    };

    const showEditRow = () => {
        if (selected?.id) {
            setTabs([
                { label: "SA", key: "account", value: selected?.account || "" },
                { label: "Description", key: "description", value: selected?.description || "" },
                { label: "Subaccount", key: "subaccount", value: selected?.subaccount === true ? "Y" : (selected?.subaccount === false ? "N" : "")  },
            ]);
            setState(s => ({ ...s, edit: true, new: false }));
            setIndex("account");
            requestAnimationFrame(() => {
                refs.current?.account?.focus?.();
            });
        }
    }

    return (
        <>
            <VirtualTable
                controller={controller}
                onWheel={(e) => (e.deltaY > 0) ? nextSection() : prevSection()}
                onRowClick={(item, event, index) => { setAlign("auto"); setSelected(item) }}
                onRowDoubleClick={() => {  callback(selected) } }
                next={nextSection}
                prev={prevSection}
                row_key={"id"}
                align="auto"
                columns={columns}
                data={tableData}
                width={560}
                height={320}
                current={selected}
                loading={loading}
                route={route}
            />
            <div className="shim" />
            {(state.new || state.edit) && <Status type="active"><InfoIcon />{drawMessage()}</Status>}
            {(!state.new && !state.edit) && <div style={{ "marginBottom": "-8px" }}><Nav nav={nav} /></div>}
        </>
    )
}